import type { Asset, Hero, PlayerAsset, PlayerScene, PlayerToken, PresentationSnapshot, Scene, WorkspaceState } from "../types";
import { playerViewTransform } from "./player-camera";
import { DEFAULT_COVER_COLOR, DEFAULT_DM_TABLE_EDGE, normalizeCoverColor } from "./table-options";
import { defaultProjectionAwareness } from "./defaults";
import { clampHeroRingScale } from "./hero-presentation";
import { isPlayerPingActive } from "./player-ping";
import { bossRevealIsActive } from "./boss-reveal";
import { arenaEffectIsActive } from "./arena-effect";
import { publicPokerState } from "./poker";
import { publicBroadsideState } from "./broadside";
import { isPlayableMapScene } from "./media-map";

function safeScene(scene: Scene, includeHiddenTokens = false, heroes: Hero[] = []): PlayerScene {
  const story = scene.story ? { title: scene.story.title, text: scene.story.text, imageAssetId: scene.story.imageAssetId } : undefined;
  const measurements = (scene.measurements ?? []).filter((item) => includeHiddenTokens || item.playerVisible);
  const areaTemplates = (scene.areaTemplates ?? []).filter((item) => includeHiddenTokens || item.playerVisible);
  const mapPins = (scene.mapPins ?? []).filter((item) => includeHiddenTokens || item.playerVisible).map(({ notes: _notes, ...pin }) => pin);
  const turnTracker = scene.turnTracker ? (() => { const activeId = scene.turnTracker!.entries[scene.turnTracker!.activeIndex]?.id; const entries = scene.turnTracker!.entries.filter((entry) => includeHiddenTokens || entry.playerVisible).map((entry) => includeHiddenTokens ? entry : ({ id: entry.id, name: entry.name, initiative: entry.initiative, playerVisible: true })); return { ...scene.turnTracker!, entries, activeIndex: Math.max(0, entries.findIndex((entry) => entry.id === activeId)) }; })() : undefined;
  const visibleTokens: PlayerToken[] = scene.tokens.filter((token) => includeHiddenTokens || (token.layer !== "gm" && (token.type === "player" || token.playerVisible))).map((token) => {
    const hero = token.type === "player" ? heroes.find((candidate) => candidate.id === token.heroId || (!token.heroId && candidate.campaignId === scene.campaignId && candidate.name === token.name && candidate.color === token.color)) : undefined;
    const playerToken = { ...token, heroRingScale: hero ? clampHeroRingScale(hero.ringScale) : token.heroRingScale }; delete playerToken.heroId; delete playerToken.locked; delete playerToken.characterSheet; return playerToken;
  });
  const geometry = includeHiddenTokens || !scene.geometry ? scene.geometry : {
    ...scene.geometry,
    walls: scene.geometry.walls.map(({ locked: _locked, ...wall }) => wall),
    objectWalls: scene.geometry.objectWalls.map(({ locked: _locked, ...wall }) => wall),
    portals: scene.geometry.portals.map((portal) => ({ id: portal.id, a: portal.a, b: portal.b, kind: portal.kind === "window" ? "window" as const : "door" as const, state: portal.state === "open" ? "open" as const : "closed" as const, blocksSight: portal.blocksSight ?? portal.kind !== "window" })),
    lights: scene.geometry.lights.map(({ locked: _locked, ...light }) => light),
  };
  const layers = includeHiddenTokens ? scene.layers : scene.layers ? Object.fromEntries(Object.entries(scene.layers).filter(([layer]) => layer !== "gm").map(([layer, value]) => [layer, { visible: value?.visible ?? true }])) : undefined;
  const materialRegions = (scene.materialRegions ?? []).map(({ name: _name, soundAssetId: _soundAssetId, soundGain: _soundGain, locked: _locked, ...region }) => region);
  const physicalObjects = (scene.physicalObjects ?? []).filter((object) => object.blocksProjection || object.blocksWeather).map(({ name: _name, locked: _locked, ...object }) => object);
  const playerSafeScene = { ...scene }; delete playerSafeScene.initialTokenPositions; delete playerSafeScene.cameraBookmarks; delete playerSafeScene.folderId; delete playerSafeScene.archived; delete playerSafeScene.mapCreator;
  delete playerSafeScene.environmentSounds; delete playerSafeScene.materialRegions; delete playerSafeScene.physicalObjects;
  const poker = scene.poker ? publicPokerState(scene.poker) : undefined;
  const broadside = scene.broadside ? publicBroadsideState(scene.broadside) : undefined;
  delete playerSafeScene.poker;
  delete playerSafeScene.broadside;
  return { ...playerSafeScene, story, geometry, layers, tokens: visibleTokens, measurements, areaTemplates, mapPins, turnTracker, materialRegions, physicalObjects, poker, broadside };
}

function projectedAssets(state: WorkspaceState, scene?: PlayerScene): PlayerAsset[] {
  const assetIds = new Set<string>();
  if (scene?.assetId) assetIds.add(scene.assetId);
  if (scene?.story?.imageAssetId) assetIds.add(scene.story.imageAssetId);
  for (const token of scene?.tokens ?? []) if (token.assetId) assetIds.add(token.assetId);
  return state.assets.filter((asset) => assetIds.has(asset.id)).map((asset: Asset) => ({
    id: asset.id, name: asset.name, kind: asset.kind, mime: asset.mime, opfsPath: asset.opfsPath,
    width: asset.width, height: asset.height, duration: asset.duration,
  }));
}

/** Local-only working snapshot: safe fields, but all tokens needed by the DM overlay. */
export function createDmPreviewSnapshot(state: WorkspaceState, scene: Scene): PresentationSnapshot {
  // The local preview keeps hidden DM objects but intentionally shares the
  // same redaction boundary as the Player Display for notes, hero links, and
  // editing-only fields. This prevents a future preview transport change from
  // exposing private campaign data.
  const safe = safeScene(scene, true, state.heroes);
  const projectedScene = isPlayableMapScene(safe) ? { ...safe, view: { ...safe.view, zoom: 1, mode: "fit" as const } } : safe;
  return {
    revision: state.presentation.revision,
    scene: projectedScene,
    assets: projectedAssets(state, projectedScene),
    calibration: state.calibrations.find((profile) => profile.id === state.settings.calibrationProfileId),
    blackout: false,
    covered: false,
    sessionWaiting: false,
    dmTableEdge: state.settings.dmTableEdge ?? DEFAULT_DM_TABLE_EDGE,
    coverColor: normalizeCoverColor(state.settings.coverColor),
    projectionAwareness: state.settings.projectionAwareness ?? defaultProjectionAwareness(),
    placementMode: false,
    preload: false,
    diceRoll: state.presentation.diceRoll,
    bossReveal: bossRevealIsActive(state.presentation.bossReveal, scene.id) ? state.presentation.bossReveal : undefined,
    arenaEffect: arenaEffectIsActive(state.presentation.arenaEffect, scene.id) ? state.presentation.arenaEffect : undefined,
    ping: state.presentation.ping?.sceneId === scene.id && isPlayerPingActive(state.presentation.ping) ? state.presentation.ping : undefined,
    generatedAt: Date.now(),
  };
}

export function createPlayerSnapshot(state: WorkspaceState, preload = false): PresentationSnapshot {
  const presentation = state.presentation;
  const calibration = state.calibrations.find((profile) => profile.id === state.settings.calibrationProfileId);
  const sessionWaiting = Boolean(!preload && presentation.sessionWaiting);
  const targetId = sessionWaiting ? undefined : preload ? presentation.stagedSceneId : (presentation.overrideSceneId ?? presentation.activeSceneId);
  const scene = state.scenes.find((candidate) => candidate.id === targetId);
  const placementMode = Boolean(!preload && presentation.tokenPlacementMode && isPlayableMapScene(scene));
  const safeTarget = scene ? safeScene(scene, false, state.heroes) : undefined;
  const cameraTarget = isPlayableMapScene(safeTarget) ? { ...safeTarget, view: playerViewTransform(scene!, calibration, Boolean(!preload && presentation.showFitScreen), state.settings.displayViewport) } : safeTarget;
  const playerScene = cameraTarget && placementMode ? {
    ...cameraTarget,
    assetId: undefined,
    waterRegions: [],
    weatherDryRegions: [],
    geometry: cameraTarget.geometry ? {
      ...cameraTarget.geometry,
      walls: [], objectWalls: [], portals: [], lights: [], ambientLight: 0,
    } : undefined,
    tokens: cameraTarget.tokens.filter((token) => token.type === "player"),
  } : cameraTarget;
  const playerTokenIds = new Set(playerScene?.tokens.map((token) => token.id) ?? []);
  const requestedCharacterAction = presentation.characterAction;
  const characterAction = !preload && requestedCharacterAction && requestedCharacterAction.sceneId === playerScene?.id
    && playerTokenIds.has(requestedCharacterAction.sourceTokenId) && playerTokenIds.has(requestedCharacterAction.targetTokenId)
    ? requestedCharacterAction : undefined;
  return {
    revision: presentation.revision,
    scene: playerScene,
    assets: projectedAssets(state, playerScene),
    calibration,
    blackout: presentation.blackout,
    covered: preload ? false : Boolean(presentation.covered),
    sessionWaiting,
    dmTableEdge: state.settings.dmTableEdge ?? DEFAULT_DM_TABLE_EDGE,
    coverColor: normalizeCoverColor(state.settings.coverColor),
    projectionAwareness: state.settings.projectionAwareness ?? defaultProjectionAwareness(),
    placementMode,
    preload,
    diceRoll: preload ? undefined : presentation.diceRoll,
    ping: !preload && presentation.ping?.sceneId === playerScene?.id && isPlayerPingActive(presentation.ping) ? presentation.ping : undefined,
    characterAction,
    bossReveal: !preload && bossRevealIsActive(presentation.bossReveal, playerScene?.id) ? presentation.bossReveal : undefined,
    arenaEffect: !preload && arenaEffectIsActive(presentation.arenaEffect, playerScene?.id) ? presentation.arenaEffect : undefined,
    generatedAt: Date.now(),
  };
}
